import PersonIcon from '@mui/icons-material/Person';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from '@mui/material';
import { Signer } from 'lib/api/business/types';
import { updateSigner } from 'lib/api/users';
import { UpdateSignerBody } from 'lib/api/users/types';
import {
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PROFESSION_OPTIONS,
} from 'lib/consts/usersFields';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import {
  buildSignerValidation,
  isEnumKey,
  isValidChileanPhone,
  isValidEmail,
  validaRut,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type SignerForm = Omit<UpdateSignerBody, 'id' | 'businessIdentifier'>;

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchSigners: () => Promise<void>;
  signer?: Signer | null;
};

const UpdateSigner: React.FC<Props> = ({
  open,
  setOpen,
  fetchSigners,
  signer,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [updatedSigner, setUpdatedSigner] = useState<SignerForm | undefined>(
    undefined,
  );

  const getBusinessIdentifier = (): string | undefined => {
    if (!signer) return undefined;
    if (signer.company.secondary) return signer.company.secondary;
    return undefined;
  };

  useEffect(() => {
    if (signer && open) {
      setUpdatedSigner({
        email: signer.email,
        name: signer.name,
        phone: signer.phone,
        identifier: signer.identifier,
        country: signer.country as SignerForm['country'],
        profession: signer.profession as SignerForm['profession'],
        maritalStatus: signer.maritalStatus as SignerForm['maritalStatus'],
      });
    } else if (!open) {
      setUpdatedSigner(undefined);
    }
  }, [signer, open]);

  const { hasError: emailHasError, message: emailErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.email?.length,
      !!updatedSigner?.email?.length && !isValidEmail(updatedSigner?.email),
      'Email obligatorio',
      'Email inválido',
    );

  const { hasError: nameHasError, message: nameErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.name?.length,
      false,
      'Nombre obligatorio',
      'Nombre inválido',
    );

  const { hasError: identifierHasError, message: identifierErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.identifier?.length,
      !!updatedSigner?.identifier?.length &&
        !validaRut(updatedSigner.identifier),
      'RUT obligatorio',
      'RUT inválido',
    );

  const { hasError: phoneHasError, message: phoneErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.phone?.length,
      !!updatedSigner?.phone?.length &&
        !isValidChileanPhone(updatedSigner.phone),
      'Teléfono obligatorio',
      'Teléfono inválido (569XXXXXXXX)',
    );

  const { hasError: countryHasError, message: countryErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.country?.length,
      !!updatedSigner?.country?.length &&
        !isEnumKey(COUNTRY_OPTIONS, updatedSigner.country),
      'País obligatorio',
      'País inválido',
    );

  const { hasError: professionHasError, message: professionErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.profession?.length,
      false,
      'Profesión obligatoria',
      'Profesión inválida',
    );

  const { hasError: maritalHasError, message: maritalErrorMessage } =
    buildSignerValidation(
      !updatedSigner?.maritalStatus?.length,
      !!updatedSigner?.maritalStatus?.length &&
        !isEnumKey(MARITAL_STATUS_OPTIONS, updatedSigner.maritalStatus),
      'Estado civil obligatorio',
      'Estado civil inválido',
    );

  const hasErrors =
    emailHasError ||
    nameHasError ||
    phoneHasError ||
    identifierHasError ||
    countryHasError ||
    professionHasError ||
    maritalHasError;

  const handleUpdateSigner = async () => {
    try {
      const businessIdentifier = getBusinessIdentifier();
      if (!businessIdentifier || !signer?.id) {
        return showAlert(
          dispatch,
          'error',
          'Problemas al actualizar el firmante',
        );
      }
      if (hasErrors) {
        let errorMessage = '';
        if (emailHasError) errorMessage = emailErrorMessage;
        else if (nameHasError) errorMessage = nameErrorMessage;
        else if (phoneHasError) errorMessage = phoneErrorMessage;
        else if (identifierHasError) errorMessage = identifierErrorMessage;
        else if (countryHasError) errorMessage = countryErrorMessage;
        else if (professionHasError) errorMessage = professionErrorMessage;
        else if (maritalHasError) errorMessage = maritalErrorMessage;
        showAlert(dispatch, 'error', errorMessage);
        return;
      }
      setLoading(true);
      await updateSigner(
        dispatch,
        {
          id: signer.id,
          ...updatedSigner,
        },
        businessIdentifier,
      );
      void fetchSigners();
      showAlert(dispatch, 'success', 'Firmante actualizado exitosamente');
      setOpen(false);
      setUpdatedSigner(undefined);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedSigner((prev: SignerForm | undefined) =>
      prev
        ? {
            ...prev,
            [e.target.name]: e.target.value,
          }
        : { [e.target.name]: e.target.value },
    );
  };

  const handleAutocompleteChange =
    (field: keyof SignerForm) =>
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: SignerForm[keyof SignerForm] | null,
    ) => {
      setUpdatedSigner((prev: SignerForm | undefined) =>
        prev
          ? {
              ...prev,
              [field]: value ?? prev[field],
            }
          : { [field]: value },
      );
    };

  const getLabel = (value: string, options: Record<string, string>) => {
    return options[value] ?? '';
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle
        onClose={() => setOpen(false)}
        title="Editar firmante"
        icon={<PersonIcon color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label="Email"
              name="email"
              value={updatedSigner?.email || ''}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={emailHasError}
              helperText={emailHasError ? emailErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              name="name"
              value={updatedSigner?.name || ''}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={nameHasError}
              helperText={nameHasError ? nameErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Teléfono"
              name="phone"
              value={updatedSigner?.phone || ''}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={phoneHasError}
              helperText={phoneHasError ? phoneErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="RUT"
              name="identifier"
              value={updatedSigner?.identifier || ''}
              onChange={handleInputChange}
              margin="dense"
              fullWidth
              error={identifierHasError}
              helperText={identifierHasError ? identifierErrorMessage : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={Object.keys(COUNTRY_OPTIONS)}
              value={updatedSigner?.country || null}
              onChange={handleAutocompleteChange('country')}
              getOptionLabel={(option) => getLabel(option, COUNTRY_OPTIONS)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nacionalidad"
                  margin="dense"
                  fullWidth
                  error={countryHasError}
                  helperText={countryHasError ? countryErrorMessage : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={PROFESSION_OPTIONS}
              value={updatedSigner?.profession || null}
              onChange={handleAutocompleteChange('profession')}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setUpdatedSigner((prev: SignerForm | undefined) =>
                    prev
                      ? {
                          ...prev,
                          profession: newInputValue,
                        }
                      : { profession: newInputValue },
                  );
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Profesión"
                  margin="dense"
                  fullWidth
                  error={professionHasError}
                  helperText={professionHasError ? professionErrorMessage : ''}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value) {
                      setUpdatedSigner((prev: SignerForm | undefined) =>
                        prev
                          ? {
                              ...prev,
                              profession: value,
                            }
                          : { profession: value },
                      );
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              options={Object.keys(MARITAL_STATUS_OPTIONS)}
              value={updatedSigner?.maritalStatus || null}
              onChange={handleAutocompleteChange('maritalStatus')}
              getOptionLabel={(option) =>
                getLabel(option, MARITAL_STATUS_OPTIONS)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Estado civil"
                  margin="dense"
                  fullWidth
                  error={maritalHasError}
                  helperText={maritalHasError ? maritalErrorMessage : ''}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={handleUpdateSigner}
          loading={loading}
        >
          Actualizar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateSigner;
